<template>
  <add-image v-if="imageForActions === '' && imageResult === ''" is-remove-bg>
    <div style="text-align: center; margin-bottom: 12px; color: var(--secondary-100)">{{ $t('menu.shadow') }}</div>
    <img :src="`${baseUrl}/images/multi-menu/shadow.png`" alt="" />
  </add-image>
  <div
    v-else-if="imageForActions !== '' && imageResult === ''"
    :class="{ 'service__container--mobile': isMobile }"
    class="service__container shadow"
  >
    <div class="shadow-edit">
      <image-finetune
        class="shadow-background-edit__finetune"
        v-if="isEdit"
        :src="imageForActions"
        @reset="isEdit = false"
        @save="editHandler"
        :actions="['reset', 'save']"
      />
      <template v-else>
        <shadow-settings />
        <div class="shadow-edit__workarea">
          <ai-card :radius="16" class="shadow-edit__workarea__card">
            <div class="shadow-edit__workarea__container" ref="imgContainerRef">
              <div
                class="shadow-edit__workarea__container__inner"
                :style="{
                  padding: computedPadding,
                  width: `${shadowParamsCanvasWidth * ratio}px`,
                  height: `${shadowParamsCanvasHeight * ratio}px`,
                  // 'min-height': `${canvasHeight * ratio}px`,
                }"
              >
                <img :src="imageForActions" alt="" :style="{ objectPosition: shadowParams.position }" />
                <ai-drop-loader v-if="generationLoading" />
              </div>
            </div>
            <!-- <v-img :src="imageForActions" class="shadow-edit__workarea__image" /> -->
            <div class="shadow-edit__workarea__data">
              <!-- <div class="shadow-edit__workarea__data__info">
              {{ imageWidth }} × {{ imageHeight }}px | {{ (imageSize / 1024).toFixed(2) }} kb
            </div> -->
              <v-spacer />
              <div class="shadow-edit__workarea__actions">
                <ai-button
                  theme="dark"
                  @click="
                    imageForActions = '';
                    imageResult = '';
                  "
                  >{{ $t('services.reset') }}</ai-button
                >
                <ai-button
                  v-if="!isMobile"
                  :disabled="generationLoading"
                  theme="gradient-primary"
                  class="shadow-edit__workarea__button"
                  @click="shadowFileHandler"
                >
                  <template v-if="!generationLoading">
                    {{ $t('shadow.create-shadow') }}
                  </template>
                  <time-counter v-else />
                </ai-button>
              </div>
            </div>
          </ai-card>
        </div>
      </template>
    </div>
  </div>
  <result-image v-else-if="imageForActions !== '' && imageResult !== ''" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventBus } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services';

// import { getBase64Size } from '@/helpers/getBase64Size';
import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';

import ShadowSettings from '@/components/pages/Shadow/ShadowSettings/ShadowSettings.vue';
import AddImage from '@/components/common/AddImage/AddImage.vue';
import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';
import ResultImage from '@/components/common/ResultImage/ResultImage.vue';
import TimeCounter from '@/components/common/TimeCounter/TimeCounter.vue';
import { sizesEn } from '@/data/outpaintSizes/sizesEn';

const paddingList = [8, 14];

const { t } = useI18n();
const baseUrl = process.env.VUE_APP_BASE_URL;

const busError = useEventBus<string>('toast-error');

const { generateImage } = useServicesStore();

const { isMobile } = storeToRefs(useCommonStore());
const {
  imageForActions,
  imageResult,
  // sourceShadowImg,
  imagePrev,
  shadowParams,
  shadowParamsCanvasWidth,
  shadowParamsCanvasHeight,
  shadowParamsFormat,
  shadowParamsType,
} = storeToRefs(useServicesStore());

const key = ref<number>(0);
const generationLoading = ref<boolean>(false);
const sizeIndex = ref<number>(0);
const customWidth = ref<number>(0);
const customHeight = ref<number>(0);
const imageWidth = ref<number>(0);
const imageHeight = ref<number>(0);
const canvasWidth = ref<number>(0);
const canvasHeight = ref<number>(0);
const imgContainerRef = ref<any>();
const isEdit = ref<boolean>(false);

const ratio = computed(() => {
  let result = 0;
  if (imgContainerRef.value) {
    if (
      imgContainerRef.value.clientWidth / imgContainerRef.value.clientheight
      > canvasWidth.value / canvasHeight.value
    ) {
      result = imgContainerRef.value.clientWidth / canvasWidth.value;
    } else {
      result = imgContainerRef.value.clientHeight / canvasHeight.value;
    }
  }
  return result;
});
// const imageSize = computed(() => getBase64Size(imageForActions.value));
const sizesList = computed(() => (sizesEn));
const computedPadding = computed(() => {
  let result;
  if (shadowParams.value.paddingIndex !== -1 && shadowParams.value.paddingIndex !== -2) {
    let customPadding = 0;
    if (shadowParamsCanvasWidth.value / shadowParamsCanvasHeight.value > imageWidth.value / imageHeight.value) {
      customPadding = (shadowParamsCanvasHeight.value / 100) * paddingList[shadowParams.value.paddingIndex];
    } else {
      customPadding = (shadowParamsCanvasWidth.value / 100) * paddingList[shadowParams.value.paddingIndex];
    }
    result = `${customPadding * ratio.value}px`;
  } else if (shadowParams.value.paddingIndex === -1) {
    result = `${shadowParams.value.padding * ratio.value}px`;
  } else if (shadowParams.value.paddingIndex === -2) {
    result = `${shadowParams.value.paddingTop * ratio.value}px ${shadowParams.value.paddingRight * ratio.value}px ${
      shadowParams.value.paddingBottom * ratio.value
    }px ${shadowParams.value.paddingLeft * ratio.value}px`;
  }
  return result;
});

const shadowFileHandler = (): void => {
  generationLoading.value = true;

  let paddingInt = {};
  if (shadowParams.value.paddingIndex === -2) {
    paddingInt = {
      left_pad: shadowParams.value.paddingLeft,
      right_pad: shadowParams.value.paddingRight,
      bottom_pad: shadowParams.value.paddingBottom,
      top_pad: shadowParams.value.paddingTop,
    };
  } else if (shadowParams.value.paddingIndex === -1) {
    paddingInt = {
      left_pad: shadowParams.value.padding,
      right_pad: shadowParams.value.padding,
      bottom_pad: shadowParams.value.padding,
      top_pad: shadowParams.value.padding,
    };
  }

  generateImage({
    action: 'shadow',
    image: removeBase64Prefix(imageForActions.value),
    params: {
      form: shadowParamsFormat.value,
      mode: shadowParamsType.value,
      padding: paddingInt,
      color: shadowParams.value.shadowColor === '' ? null : shadowParams.value.shadowColor,
      align: shadowParams.value.position,
      shadow: shadowParams.value.isShadow,
      size: {
        height: shadowParamsCanvasHeight.value,
        width: shadowParamsCanvasWidth.value,
      },
    },
  })
    .then((res) => {
      imagePrev.value = imageForActions.value;
      imageForActions.value = `data:image/png;base64,${res.payload.imageWithPadding}`;
      imageResult.value = `data:image/png;base64,${res.image}`;
      // sourceShadowImg.value = `data:image/png;base64,${res.payload.imageWithPadding}`;
    })
    .catch((e) => {
      busError.emit(e?.error || t('common.err-gen'));
    })
    .finally(() => {
      generationLoading.value = false;
    });
};

const setSize = (index: number): void => {
  sizeIndex.value = index;
  if (index === -1) {
    canvasWidth.value = customWidth.value;
    canvasHeight.value = customHeight.value;
  } else {
    canvasWidth.value = sizesList.value[index].width;
    canvasHeight.value = sizesList.value[index].height;
  }
};

const imageSizeHandler = (): void => {
  if (imageForActions.value !== '') {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
      customWidth.value = img.width;
      customHeight.value = img.height;
    };
    img.src = imageForActions.value;
    key.value += 1;
  }
};

const editHandler = (image: string): void => {
  imageForActions.value = image;
  isEdit.value = false;
};

onMounted(() => {
  imageSizeHandler();
  setTimeout(() => {
    setSize(0);
  }, 100);
});

watch(imageForActions, () => {
  imageSizeHandler();
  setTimeout(() => {
    setSize(0);
  }, 100);
});

watch(imageResult, () => {
  if (imageResult.value === '' && imagePrev.value !== '') {
    imageForActions.value = imagePrev.value;
  }
});

watch(customHeight, (newVal) => {
  canvasHeight.value = newVal;
});

watch(customWidth, (newVal) => {
  canvasWidth.value = newVal;
});
</script>

<style scoped lang="scss" src="./Shadow.scss" />
